<script setup>
import {gameScoreText, scorePoints} from '~/utils/gameScore';
// One meet match, exactly as the app draws it (Flutter MeetMatchCard): a
// title row with the actions on the right, the two sides as plain player lists
// with a quiet "vs" between them (winning side's players carry a green dot), and — once a
// result is in — a result strip UNDER the boxes: each side's result beneath
// its own team (winner bold in success), the game scores as pills in the
// middle, and the played time. Slots let the callers supply their own player
// rows (tap-to-view vs plain) and header actions (Enter result / View match).
import {computed} from 'vue';
import {useNuxtApp} from '#app';
import PlayerCard from '~/components/players/PlayerCard.vue';

const props = defineProps({
  fixture: {type: Object, required: true},
  title: {type: String, required: true},
});

const {$luxon} = useNuxtApp();

const hasResult = computed(() =>
    props.fixture.homeTeamResult != null && props.fixture.awayTeamResult != null);

const games = computed(() =>
    [...(props.fixture.games ?? [])].sort((a, b) => a.sort - b.sort));

// The winner is ALWAYS decided by the points awarded — never by parsing the
// result text, which can be anything a scoring system stores ("Win", "W", 3).
const won = (side) => {
  if (!hasResult.value) return false;
  const home = props.fixture.homeTeamPoints;
  const away = props.fixture.awayTeamPoints;
  if (home == null || away == null || home === away) return false;
  return side === 'home' ? home > away : away > home;
};

// Each side's figure is the STORED result, exactly as the app shows it —
// "3", "Win", "W" — never rewritten into games won, so the two surfaces
// always read identically for the same match.
const sideResult = (side) =>
    side === 'home' ? props.fixture.homeTeamResult : props.fixture.awayTeamResult;

const playedText = computed(() => {
  if (!hasResult.value || !props.fixture.playedAt) return null;
  return `Played ${$luxon.fromISO(props.fixture.playedAt, {zone: 'utc'}).toLocal().toFormat('ccc d MMM, HH:mm')}`;
});
</script>

<template>
  <div class="mmc">
    <div class="mmc-head">
      <span class="mmc-title">{{ title }}</span>
      <div class="mmc-acts"><slot name="actions"/></div>
    </div>
    <div class="mmc-teams">
      <div class="mmc-col" :class="{'mmc-col--won': won('home')}">
        <div v-for="p in (fixture.homeTeam?.players ?? [])" :key="p.id" class="mmc-pc">
          <player-card :player="p" :max-lines="1"/>
          <span v-if="won('home')" class="mmc-dot" title="Won"/>
        </div>
      </div>
      <div class="mmc-vs">vs</div>
      <div class="mmc-col" :class="{'mmc-col--won': won('away')}">
        <div v-for="p in (fixture.awayTeam?.players ?? [])" :key="p.id" class="mmc-pc">
          <player-card :player="p" :max-lines="1"/>
          <span v-if="won('away')" class="mmc-dot" title="Won"/>
        </div>
      </div>
    </div>
    <!-- result strip: home result | game pills | away result, each side's
         figure under its own team, winner bold in success -->
    <div v-if="hasResult" class="mmc-result">
      <div class="mmc-result__row">
        <span class="mmc-side" :class="{'mmc-side--won': won('home')}">{{ sideResult('home') }}</span>
        <div v-if="games.length" class="mmc-games">
          <span v-for="game in games" :key="game.id" class="mmc-game">
            <b :class="{'mmc-game--won': game.homeTeamScore > game.awayTeamScore}">{{ scorePoints(game.homeTeamScore) }}</b>–<b
              :class="{'mmc-game--won': game.awayTeamScore > game.homeTeamScore}">{{ scorePoints(game.awayTeamScore) }}</b>
          </span>
        </div>
        <span v-else class="mmc-dash">–</span>
        <span class="mmc-side" :class="{'mmc-side--won': won('away')}">{{ sideResult('away') }}</span>
      </div>
      <div v-if="playedText" class="mmc-played">{{ playedText }}</div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.mmc {
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 12px;
  overflow: hidden;

  & + .mmc {
    margin-top: 10px;
  }
}

.mmc-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 7px 8px 7px 12px;
  border-bottom: 1px solid rgb(var(--v-theme-borderLight));
}

.mmc-title {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: rgb(var(--v-theme-textSubtle));
}

.mmc-acts {
  display: flex;
  align-items: center;
  gap: 6px;
}

.mmc-teams {
  display: flex;
  align-items: center;
  gap: 0;
  padding: 10px;
}

/* Sides are plain lists (no boxes, no fills — Grant, 2026-09-15); the winning
   side's players carry a green dot. Same on the app. */
.mmc-col {
  flex: 1;
  min-width: 0;
}

.mmc-pc {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 10px;

  > :first-child { min-width: 0; flex: 1; }
}

.mmc-dot {
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgb(var(--v-theme-success));
}

.mmc-vs {
  flex-shrink: 0;
  min-width: 34px;
  text-align: center;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.3px;
  color: rgb(var(--v-theme-textSubtle));
}

/* result strip — three zones (3:4:3) so each figure sits under its own team */
.mmc-result {
  padding: 10px 12px;
  background: rgb(var(--v-theme-background));
  border-top: 1px solid rgb(var(--v-theme-borderLight));
}

.mmc-result__row {
  display: flex;
  align-items: center;
}

.mmc-side {
  flex: 3;
  text-align: center;
  font-size: 18px;
  font-weight: 600;
  letter-spacing: -0.3px;
  color: rgb(var(--v-theme-textSubtle));

  &--won {
    font-weight: 800;
    color: rgb(var(--v-theme-success));
  }
}

.mmc-games {
  flex: 4;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 5px;
}

.mmc-game {
  padding: 3px 7px;
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 7px;
  font-size: 11.5px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: rgb(var(--v-theme-textSubtle));

  b {
    font-weight: 700;
    color: rgb(var(--v-theme-textSubtle));
  }

  .mmc-game--won {
    color: rgb(var(--v-theme-success));
  }
}

.mmc-dash {
  flex: 4;
  text-align: center;
  font-size: 16px;
  font-weight: 700;
  color: rgb(var(--v-theme-textSubtle));
}

.mmc-played {
  margin-top: 6px;
  text-align: center;
  font-size: 10.5px;
  color: rgb(var(--v-theme-textSubtle));
}
</style>
